import { Button } from '@/components/Button';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FormValue, SearchFormInput } from '@/features/agent/provided_job_posts/searchSchema';
import { searchSchema } from '@/features/agent/provided_job_posts/searchSchema';
import { useState, useMemo, useCallback } from 'react';
import type { SearchQuery } from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';
import useProvidedJobPosts from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';
import Loading from '@/components/Loading';
import CandidatesProvidedJobPostCard from './CandidatesProvidedJobPostCard';
import CandidatesProvidedJobPostModalSearchForm from './CandidatesProvidedJobPostModalSearchForm';
import type {
  AgentApiProvidedJobPostsRecord,
  AgentApiSharedJobPostsMetadata,
  AgentApiSharedJobPostsItem,
  AgentApiCandidateEndpointsCopySourceCandidate,
} from '@/types/serializers';
import { useQuery } from '@tanstack/react-query';
import useSharedJobPosts from '@/features/agent/shared_job_posts/hooks/useSharedJobPosts';
import type { SearchQuery as SharedSearchQuery } from '@/features/agent/shared_job_posts/hooks/useSharedJobPosts';
import SharedJobPostCard from '@/features/agent/shared_job_posts/components/SharedJobPostCard';
import CandidatesSharedJobPostModalSearchForm from './CandidatesSharedJobPostModalSearchForm';
import CandidatesJobPostEndpointsCopyContents from './CandidatesJobPostEndpointsCopyContents';

type Props = {
  setStep: (step: 'confirm') => void;
  close: () => void;
  selectedJobPosts: AgentApiProvidedJobPostsRecord[];
  setSelectedJobPosts: (jobPosts: AgentApiProvidedJobPostsRecord[]) => void;
  selectedSharedJobPosts: AgentApiSharedJobPostsItem[];
  setSelectedSharedJobPosts: (jobPosts: AgentApiSharedJobPostsItem[]) => void;
  // 「別の求職者からコピー」タブ用（提案先が単一の場合のみ指定。一括提案では省略しタブ非表示）
  candidateProfileId?: number;
  copySourceCandidate?: AgentApiCandidateEndpointsCopySourceCandidate | null;
  setCopySourceCandidate?: (
    candidate: AgentApiCandidateEndpointsCopySourceCandidate | null
  ) => void;
};

type TabValue = 'own' | 'shared' | 'copy';

export default function CandidatesJobPostEndpointsNewModalAddContents({
  setStep,
  close,
  candidateProfileId,
  selectedJobPosts,
  setSelectedJobPosts,
  selectedSharedJobPosts,
  setSelectedSharedJobPosts,
  copySourceCandidate,
  setCopySourceCandidate,
}: Props) {
  const { data: metadata } = useQuery<AgentApiSharedJobPostsMetadata>({
    queryKey: ['/agent/api/shared_job_posts/metadata'],
    queryFn: async () => {
      const response = await fetch('/agent/api/shared_job_posts/metadata.json');
      if (!response.ok) {
        throw new Error('Failed to fetch metadata');
      }
      return response.json();
    },
  });

  const shouldShowSharedTab =
    metadata?.permitted_providers && metadata.permitted_providers.length > 0;

  // 提案先が単一のときのみ「別の求職者からコピー」タブを表示する
  const canCopy =
    candidateProfileId != null &&
    copySourceCandidate !== undefined &&
    setCopySourceCandidate != null;

  // 確認画面から戻った際、選択済みの内容に対応するタブを復元する
  const [currentTab, setCurrentTab] = useState<TabValue>(() =>
    copySourceCandidate ? 'copy' : selectedSharedJobPosts.length > 0 ? 'shared' : 'own'
  );

  const form = useForm<SearchFormInput, unknown, FormValue>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      companyIds: [],
      salary: '',
      minSalary: '',
      maxSalary: '',
      jobPositions: '',
      prefectureCodes: [],
      customTagIds: [],
      customTagMatch: 'all',
      listId: undefined,
      searchColumnAlpha: 'title',
      searchKeywordAlpha: '',
      searchOperatorAlpha: 'or',
      searchColumnBeta: 'mandatory_requirement',
      searchKeywordBeta: '',
      searchOperatorBeta: 'or',
      searchColumnGamma: 'keyword_content',
      searchKeywordGamma: '',
      searchOperatorGamma: 'or',
      isIncludeArchived: false,
      isSalaryEmpty: false,
      sort: 'updated_at:desc',
      jobSourceId: undefined,
    },
  });

  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    company_ids: [] as number[],
    job_position_name: '',
    prefecture_codes: [] as number[],
    min_salary: '',
    max_salary: '',
    salary: '',
    custom_tag_ids: [] as number[],
    list_ids: [] as number[],
    search_column_alpha: '',
    search_keyword_alpha: '',
    search_operator_alpha: '',
    search_column_beta: '',
    search_keyword_beta: '',
    search_operator_beta: '',
    search_column_gamma: '',
    search_keyword_gamma: '',
    search_operator_gamma: '',
    is_include_archived: false,
    sort: 'updated_at:desc',
    job_source_id: undefined,
    is_available: true,
  });

  const [sharedSearchQuery, setSharedSearchQuery] = useState<SharedSearchQuery>({
    search_query: '',
    company_name: '',
    job_position_name: '',
    min_salary: '',
    max_salary: '',
    salary: '',
    sort: 'updated_at:desc',
    provider: undefined,
    shared_job_post_list_id: undefined,
  });

  const onSubmit: SubmitHandler<FormValue> = (data) => {
    const sq = {
      company_ids: data.companyIds,
      job_position_name: data.jobPositions.trim(),
      prefecture_codes: data.prefectureCodes,
      min_salary: String(data.minSalary),
      max_salary: String(data.maxSalary),
      salary: String(data.salary),
      custom_tag_ids: data.customTagIds,
      custom_tag_match: data.customTagMatch,
      list_ids: data.listId ? [Number(data.listId)] : [],
      search_column_alpha: data.searchColumnAlpha,
      search_keyword_alpha: data.searchKeywordAlpha ?? '',
      search_operator_alpha: data.searchOperatorAlpha,
      search_column_beta: data.searchColumnBeta,
      search_keyword_beta: data.searchKeywordBeta ?? '',
      search_operator_beta: data.searchOperatorBeta,
      search_column_gamma: data.searchColumnGamma,
      search_keyword_gamma: data.searchKeywordGamma ?? '',
      search_operator_gamma: data.searchOperatorGamma,
      is_include_archived: data.isIncludeArchived,
      sort: data.sort,
      job_source_id: data.jobSourceId,
      is_available: true,
    };

    setSearchQuery(sq);
  };

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProvidedJobPosts(searchQuery);

  const {
    data: sharedData,
    isLoading: sharedIsLoading,
    fetchNextPage: sharedFetchNextPage,
    hasNextPage: sharedHasNextPage,
    isFetchingNextPage: sharedIsFetchingNextPage,
  } = useSharedJobPosts(sharedSearchQuery);

  const providedJobPosts = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.records);
  }, [data?.pages]);

  const sharedJobPosts = useMemo(() => {
    if (!sharedData?.pages) return [];
    return sharedData.pages.flatMap((page) => page.records);
  }, [sharedData?.pages]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (isFetchingNextPage || !hasNextPage) return;

      const element = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = element;

      if (scrollTop + clientHeight >= scrollHeight - 500) {
        fetchNextPage();
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleSelectionChange = (jobPost: AgentApiProvidedJobPostsRecord, selected: boolean) => {
    if (selected) {
      setSelectedJobPosts([...selectedJobPosts, jobPost]);
    } else {
      setSelectedJobPosts(selectedJobPosts.filter((jp) => jp.id !== jobPost.id));
    }
  };

  const isSelected = (jobPost: AgentApiProvidedJobPostsRecord) => {
    return selectedJobPosts.some((jp) => jp.id === jobPost.id);
  };

  const handleSharedScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (sharedIsFetchingNextPage || !sharedHasNextPage) return;

      const element = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = element;

      if (scrollTop + clientHeight >= scrollHeight - 500) {
        sharedFetchNextPage();
      }
    },
    [sharedIsFetchingNextPage, sharedHasNextPage, sharedFetchNextPage]
  );

  const handleSharedSelectionChange = (jobPost: AgentApiSharedJobPostsItem, selected: boolean) => {
    if (selected) {
      setSelectedSharedJobPosts([...selectedSharedJobPosts, jobPost]);
    } else {
      setSelectedSharedJobPosts(selectedSharedJobPosts.filter((jp) => jp.id !== jobPost.id));
    }
  };

  const isSharedSelected = (jobPost: AgentApiSharedJobPostsItem) => {
    return selectedSharedJobPosts.some((jp) => jp.id === jobPost.id);
  };

  const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (currentTab === 'own') {
      handleScroll(e);
    } else if (currentTab === 'shared') {
      handleSharedScroll(e);
    }
  };

  const hasSelection =
    currentTab === 'shared' ? selectedSharedJobPosts.length > 0 : selectedJobPosts.length > 0;

  return (
    <FormProvider {...form}>
      <div className="flex h-[720px] max-h-[80vh] w-[1100px] max-w-[95vw] flex-col rounded bg-white">
        <div className="flex h-[56px] shrink-0 items-center border-b border-solid border-border px-lg">
          <p className="text-base font-bold">提案求人の追加</p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto" onScroll={handleContentScroll}>
          {/* タブ */}
          <div className="flex h-auto items-center px-lg py-md">
            <div className="flex h-[36px]">
              <button
                type="button"
                onClick={() => setCurrentTab('own')}
                className={`flex items-center justify-center border border-solid border-border px-2 ${
                  currentTab === 'own'
                    ? 'bg-[#EDF8FF] font-bold text-primary'
                    : 'bg-white font-normal text-[#6D6D6D]'
                } rounded-l text-sm`}
              >
                自社求人
              </button>
              {shouldShowSharedTab && (
                <button
                  type="button"
                  onClick={() => setCurrentTab('shared')}
                  className={`flex items-center justify-center border border-l-0 border-solid border-border px-2 ${
                    currentTab === 'shared'
                      ? 'bg-[#EDF8FF] font-bold text-primary'
                      : 'bg-white font-normal text-[#6D6D6D]'
                  } text-sm`}
                >
                  求人データベース
                </button>
              )}
              {canCopy && (
                <button
                  type="button"
                  onClick={() => setCurrentTab('copy')}
                  className={`flex items-center justify-center border border-l-0 border-solid border-border px-2 ${
                    currentTab === 'copy'
                      ? 'bg-[#EDF8FF] font-bold text-primary'
                      : 'bg-white font-normal text-[#6D6D6D]'
                  } rounded-r text-sm`}
                >
                  別の求職者からコピー
                </button>
              )}
            </div>
          </div>
          {/* 自社求人タブのコンテンツ */}
          {currentTab === 'own' && (
            <div className="p-lg pt-0">
              <div className="mb-sm">
                <CandidatesProvidedJobPostModalSearchForm onSubmit={onSubmit} />
              </div>

              <div className="mt-sm">
                {isLoading ? (
                  <div className="flex min-h-[400px] items-center justify-center">
                    <Loading size="small" />
                  </div>
                ) : (
                  <>
                    {providedJobPosts.length > 0 && (
                      <div className="flex flex-col gap-lg">
                        {providedJobPosts.map((p) => (
                          <CandidatesProvidedJobPostCard
                            key={p.id}
                            providedJobPost={p}
                            isSelected={isSelected(p)}
                            onSelectionChange={(selected) => handleSelectionChange(p, selected)}
                          />
                        ))}
                      </div>
                    )}
                    {isFetchingNextPage && providedJobPosts.length > 0 && (
                      <div className="flex justify-center py-md">
                        <Loading size="small" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* 求人データベースタブのコンテンツ */}
          {currentTab === 'shared' && (
            <div className="p-lg pt-0">
              <div className="mb-sm">
                <CandidatesSharedJobPostModalSearchForm
                  searchQuery={sharedSearchQuery}
                  onSearchQueryChange={setSharedSearchQuery}
                />
              </div>

              <div className="mt-sm">
                {sharedIsLoading ? (
                  <div className="flex min-h-[400px] items-center justify-center">
                    <Loading size="small" />
                  </div>
                ) : (
                  <>
                    {sharedJobPosts.length > 0 && (
                      <div className="flex flex-col gap-lg">
                        {sharedJobPosts.map((p) => (
                          <SharedJobPostCard
                            key={p.id}
                            post={p}
                            isChecked={isSharedSelected(p)}
                            onCheckedChange={(checked) => handleSharedSelectionChange(p, checked)}
                            disableDetail
                            hideShare
                          />
                        ))}
                      </div>
                    )}
                    {sharedIsFetchingNextPage && sharedJobPosts.length > 0 && (
                      <div className="flex justify-center py-md">
                        <Loading size="small" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* 別の求職者からコピータブのコンテンツ */}
          {currentTab === 'copy' && canCopy && (
            <div className="p-lg pt-0">
              <CandidatesJobPostEndpointsCopyContents
                candidateProfileId={candidateProfileId!}
                sourceCandidate={copySourceCandidate!}
                setSourceCandidate={setCopySourceCandidate!}
                selectedJobPosts={selectedJobPosts}
                setSelectedJobPosts={setSelectedJobPosts}
              />
            </div>
          )}
        </div>
        {hasSelection && (
          <div className="flex h-[74px] shrink-0 items-center justify-end gap-md border-t border-solid border-border px-lg">
            <Button color="secondary" onClick={close}>
              キャンセル
            </Button>
            <Button onClick={() => setStep('confirm')}>提案求人を確認</Button>
          </div>
        )}
      </div>
    </FormProvider>
  );
}
